import React, { useState, useMemo, useEffect } from "react";
import { MdEdit, MdDelete } from "react-icons/md";
import PageLayout from "../../../Layouts/PageLayout/PageLayout";
import Table from "../../../components/common/Table/Table";
import Button from "../../../components/common/Button/Button";
import InputSearch from "../../../components/common/Inputs/InputSearch/InputSearch";
import { FiDownload } from "react-icons/fi";
import { GrAdd } from "react-icons/gr";
import { LuArrowDownUp } from "react-icons/lu";
import { getApiInstance } from "../../../utils/api";
import { mapWithReferences, mapList } from "../../../utils/utils";
import { useZone } from "../../../context/ZoneContext";
import { useTranslation } from "react-i18next";

export default function Provincias({ user }) {
    const { t } = useTranslation();
    const [provincias, setProvincias] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [comunidades, setComunidades] = useState([]);
    const [paises, setPaises] = useState([]);
    const { selectedZone } = useZone();

    const filterFields = [
        { key: "provincia", label: t("fields.province"), type: "multiselect" },
        { key: "comunidad", label: t("fields.community"), type: "multiselect" },
        { key: "pais", label: t("fields.country"), type: "multiselect" },
    ];

    useEffect(() => {
        fetchPaises();
        fetchComunidades();
        fetchProvincias();
    }, [selectedZone]);

    const columnasProvincias = [
        { key: "id", label: "Provincia", visible: false },
        { key: "codProvincia", label: "Provincia", visible: false },
        {
            key: "provincia",
            label: "Provincia",
            visible: true,
            sortable: true,
        },
        { key: "codComunidad", label: "Comunidad", visible: false },
        {
            key: "comunidad",
            label: "Comunidad",
            visible: true,
            sortable: true,
            visibleOnExpand: true,
        },
        {
            key: "pais",
            label: "País",
            visible: true,
            sortable: true,
            visibleOnExpand: true,
        },
        { key: "codPais", label: "País", visible: false },
    ];

    useEffect(() => {
        fetchPaises();
        fetchComunidades();
        fetchProvincias();
    }, [selectedZone]);

    const fetchComunidades = async () => {
        setLoading(true);
        const api = getApiInstance();

        try {
            let comunidadesData = [];

            const response = await api.get(
                `/api/Comunidades/GetComunidadesByZona/${selectedZone.value}`
            );
            comunidadesData = response.data;

            setComunidades([
                ...mapList(
                    comunidadesData,
                    "comunidad",
                    "codComunidad",
                    "codComunidad",
                    "codPais"
                ),
            ]);

            console.log("Comunidades data fetched:", comunidadesData);

            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar las comunidades. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchPaises = async () => {
        setLoading(true);
        const api = getApiInstance();

        try {
            let paisesData = [];

            const response = await api.get(
                `/api/Paises/GetPaisesByZona/${selectedZone.value}`
            );
            paisesData = response.data;

            setPaises(mapList(paisesData, "pais", "codPais", "codPais"));

            console.log("Paises data fetched:", paisesData);

            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar los países. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchProvincias = async () => {
        setLoading(true);
        const api = getApiInstance();
        try {
            let provinciasData = [];

            const response = await api.get(
                `/api/Provincias/GetProvinciasByZona/${selectedZone.value}`
            );
            provinciasData = response.data;

            setProvincias(provinciasData);
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar las provincias. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    if (error)
        return (
            <div className="muebles-pages__error-message">Error: {error}</div>
        );

    return (
        <PageLayout
            title={t("provinces.title")}
            subtitle={t("provinces.subtitle")}
        >
            <Table
                columns={columnasProvincias}
                dataTable={provincias}
                showCheckboxes={true}
                expandable={true}
                expandableBreakpoint={600}
                noResultsMessage={t("provinces.noProvincesFound")}
                actions={["Eliminar", "Editar"]}
                searchTerms={[
                    t("fields.province"),
                    t("fields.community"),
                    t("fields.country"),
                ]}
                elementName={t("fields.province")}
                formElements={[
                    {
                        elements: [
                            {
                                name: "provincia",
                                type: "text",
                                label: t("fields.province"),
                                placeholder: t("provinces.enterProvince"),
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "codPais",
                                type: "select",
                                label: t("fields.country"),
                                placeholder: t("placeholders.selectCountry"),
                                options: paises,
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "codComunidad",
                                type: "select",
                                label: t("fields.community"),
                                placeholder: t("placeholders.selectCommunity"),
                                options: comunidades,
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                                associatedFormElement: "codPais",
                            },
                        ],
                    },
                ]}
                filterFields={filterFields}
                loading={loading}
                keyProp="codProvincia"
                sortProp="provincia"
                addApiUrl="/api/Provincias/SetProvincia"
                editApiUrl="/api/Provincias/EditProvincia"
                deleteApiUrl="/api/Provincias/DeleteProvincia"
                userCodAcceso={Number(user.codAcceso)}
                userCodPerfil={Number(user.role)}
            />
        </PageLayout>
    );
}
